import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BusinessActions from '../../Redux/actions/business-actions';
import PlanGroupActions from '../../Redux/transition/plan-group/plan-group.reducer';

const normalizePlanGroups = (data) => {
    const groups = data?.groups;
    if (!Array.isArray(groups)) return [];
    return groups
        .map((item) => item?.group ?? item)
        .filter((group) => group?.id && group?.name)
        .map((group) => ({ id: group.id, name: group.name }));
};

const normalizeExclusions = (data) => {
    if (!Array.isArray(data)) return [];
    return data
        .map((exclusion) => ({
            id: exclusion?.id,
            planGroupId: exclusion?.planGroup?.id,
            name: exclusion?.planGroup?.name,
        }))
        .filter((exclusion) => exclusion.id && exclusion.name);
};

export const SignupReqExclusionsSection = ({ businessId, showToast }) => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const saveSubmittedRef = useRef(false);
    const deleteSubmittedRef = useRef(false);

    const {
        signupReqExclusions,
        fetchingSignupReqExclusions,
        creatingSignupReqExclusions,
        deletingSignupReqExclusion,
        errorCreateSignupReqExclusions,
        errorDeleteSignupReqExclusion,
    } = useSelector((state) => state.businesses);
    const {
        planGroupsGraphedActive,
        fetchingGraphedActive,
    } = useSelector((state) => state.planGroups);

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [selectedPlanGroupIds, setSelectedPlanGroupIds] = useState([]);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [exclusionToRemove, setExclusionToRemove] = useState(null);

    const getActivePlanGroups = useCallback(
        (id) => dispatch(PlanGroupActions.planGroupGraphedActiveRequest(id)),
        [dispatch]
    );
    const saveExclusions = useCallback(
        (id, planGroupIds) => dispatch(BusinessActions.createSignupReqExclusionsRequest(id, planGroupIds)),
        [dispatch]
    );
    const removeExclusion = useCallback(
        (id, exclusionId) => dispatch(BusinessActions.deleteSignupReqExclusionRequest(id, exclusionId)),
        [dispatch]
    );

    const exclusions = useMemo(
        () => normalizeExclusions(signupReqExclusions),
        [signupReqExclusions]
    );
    const excludedPlanGroupIds = useMemo(
        () => new Set(exclusions.map((exclusion) => String(exclusion.planGroupId))),
        [exclusions]
    );
    const availablePlanGroups = useMemo(
        () => normalizePlanGroups(planGroupsGraphedActive)
            .filter((group) => !excludedPlanGroupIds.has(String(group.id))),
        [planGroupsGraphedActive, excludedPlanGroupIds]
    );

    useEffect(() => {
        if (creatingSignupReqExclusions) return;
        if (!saveSubmittedRef.current) return;

        if (errorCreateSignupReqExclusions) {
            saveSubmittedRef.current = false;
            showToast('error', t('signupRequirements.exclusions_save_error'));
            return;
        }

        saveSubmittedRef.current = false;
        setShowAddDialog(false);
        setSelectedPlanGroupIds([]);
        setSaveAttempted(false);
        showToast('success', t('signupRequirements.exclusions_save_success'));
    }, [
        creatingSignupReqExclusions,
        errorCreateSignupReqExclusions,
        showToast,
        t,
    ]);

    useEffect(() => {
        if (deletingSignupReqExclusion) return;
        if (!deleteSubmittedRef.current) return;

        if (errorDeleteSignupReqExclusion) {
            deleteSubmittedRef.current = false;
            showToast('error', t('signupRequirements.exclusions_delete_error'));
            return;
        }

        deleteSubmittedRef.current = false;
        setExclusionToRemove(null);
        showToast('success', t('signupRequirements.exclusions_delete_success'));
    }, [
        deletingSignupReqExclusion,
        errorDeleteSignupReqExclusion,
        showToast,
        t,
    ]);

    const handleOpenAddDialog = () => {
        setSelectedPlanGroupIds([]);
        setSaveAttempted(false);
        getActivePlanGroups(businessId);
        setShowAddDialog(true);
    };

    const handleCloseAddDialog = () => {
        if (creatingSignupReqExclusions) return;
        setShowAddDialog(false);
        setSelectedPlanGroupIds([]);
        setSaveAttempted(false);
    };

    const handlePlanGroupToggle = (planGroupId, checked) => {
        setSelectedPlanGroupIds((current) => {
            if (checked) {
                return current.some((groupId) => String(groupId) === String(planGroupId))
                    ? current
                    : [...current, planGroupId];
            }
            return current.filter((groupId) => String(groupId) !== String(planGroupId));
        });
    };

    const handleSaveExclusions = () => {
        setSaveAttempted(true);
        if (!selectedPlanGroupIds.length) return;
        saveSubmittedRef.current = true;
        saveExclusions(businessId, selectedPlanGroupIds);
    };

    const handleConfirmRemove = () => {
        if (!exclusionToRemove?.id) return;
        deleteSubmittedRef.current = true;
        removeExclusion(businessId, exclusionToRemove.id);
    };

    const addDialogFooter = () => (
        <div className="flex justify-content-end">
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text"
                onClick={handleCloseAddDialog}
                disabled={creatingSignupReqExclusions}
            />
            <Button
                label={t('signupRequirements.save')}
                icon="pi pi-check"
                loading={creatingSignupReqExclusions}
                disabled={!selectedPlanGroupIds.length}
                onClick={handleSaveExclusions}
            />
        </div>
    );

    const deleteFooter = () => (
        <div className="flex justify-content-end">
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => setExclusionToRemove(null)}
                disabled={deletingSignupReqExclusion}
            />
            <Button
                label={t('common.ok')}
                icon="pi pi-check"
                loading={deletingSignupReqExclusion}
                onClick={handleConfirmRemove}
            />
        </div>
    );

    return (
        <div className="mt-3">
            <div className="flex align-items-center justify-content-between gap-2">
                <p className="font-bold text_line m-0">{t('signupRequirements.exclusions_title')}</p>
                <Button
                    label={t('signupRequirements.add_exclusions')}
                    icon="pi pi-plus"
                    className="p-button-text p-button-sm"
                    onClick={handleOpenAddDialog}
                    disabled={creatingSignupReqExclusions || deletingSignupReqExclusion}
                />
            </div>
            <p className="text_line mt-1">{t('signupRequirements.exclusions_description')}</p>
            {fetchingSignupReqExclusions ? (
                <span className="text-sm">{t('signupRequirements.loading_exclusions')}</span>
            ) : exclusions.length ? (
                <ul className="m-0 pl-3">
                    {exclusions.map((exclusion) => (
                        <li key={exclusion.id} className="text-sm flex align-items-center gap-2 mb-1">
                            <span>{exclusion.name}</span>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded p-button-sm"
                                aria-label={t('signupRequirements.exclusions_delete_header')}
                                onClick={() => setExclusionToRemove(exclusion)}
                                disabled={deletingSignupReqExclusion}
                            />
                        </li>
                    ))}
                </ul>
            ) : (
                <span className="text-sm">{t('signupRequirements.exclusions_empty')}</span>
            )}

            <Dialog
                visible={showAddDialog}
                onHide={handleCloseAddDialog}
                header={t('signupRequirements.select_exclusions_header')}
                footer={addDialogFooter()}
                style={{ width: '640px' }}
            >
                <p className="text_line">{t('signupRequirements.select_exclusions_description')}</p>
                {fetchingGraphedActive ? (
                    <p>{t('signupRequirements.loading_plan_groups')}</p>
                ) : !availablePlanGroups.length ? (
                    <p>{t('signupRequirements.no_plan_groups')}</p>
                ) : (
                    <div className="mt-3">
                        {availablePlanGroups.map((group) => (
                            <div key={group.id} className="flex align-items-center mb-2">
                                <Checkbox
                                    inputId={`signup-req-exclusion-${group.id}`}
                                    checked={selectedPlanGroupIds.some(
                                        (groupId) => String(groupId) === String(group.id)
                                    )}
                                    onChange={(e) => handlePlanGroupToggle(group.id, e.checked)}
                                />
                                <label
                                    htmlFor={`signup-req-exclusion-${group.id}`}
                                    className="ml-2 mb-0 cursor-pointer"
                                >
                                    {group.name}
                                </label>
                            </div>
                        ))}
                    </div>
                )}
                {saveAttempted && !selectedPlanGroupIds.length && (
                    <p className="mt-2 text-red-500">{t('signupRequirements.plan_groups_required')}</p>
                )}
            </Dialog>

            <ConfirmDialog
                visible={!!exclusionToRemove}
                onHide={() => setExclusionToRemove(null)}
                header={t('signupRequirements.exclusions_delete_header')}
                footer={deleteFooter()}
                style={{ width: '350px' }}
                message={t('signupRequirements.exclusions_delete_confirmation')}
            />
        </div>
    );
};
